// A searchable single choice for long lists (DES1 UX punch list ST-03): App
// language and Time zone in Settings → Preferences.
//
// The WAI-ARIA editable combobox with list autocomplete. Typing filters the
// list, ↑/↓ and Page Up/Down move the active option, Enter chooses it, and
// Escape or leaving puts the chosen value back without changing anything: the
// value changes only on a choice, so a half-typed filter is never saved. A
// value missing from the list is the caller's to pass as an option, so it is
// still shown and kept.
//
// Wide screens: the field itself is the combobox and the list opens under it.
// Phones (≤ 640 px): the field is a button that opens the kit BottomSheet with
// its own search field and the list, so the keyboard never covers the choices;
// the sheet traps focus, closes on Escape and returns focus to the button. The
// search and listbox semantics stay here; the sheet is only the container.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { BottomSheet } from '@/components/ui/sheet';
import searchCopy from '@/i18n/ns/search';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { formatCount } from '@/i18n/format';
import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNarrowViewport } from '@/lib/mobile';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  value: string;
  label: string;
}

/** Lower-case, accent-free, `_`/`/` as spaces and − as -, so "sao paulo" finds America/Sao_Paulo. */
const fold = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[_/]/g, ' ')
    // A typographic minus (U+2212) matches a typed hyphen: "utc-3" finds "UTC−3".
    .replace(/\u2212/g, '-')
    .toLowerCase();

export function filterComboboxOptions(
  options: ReadonlyArray<ComboboxOption>,
  query: string,
): ReadonlyArray<ComboboxOption> {
  const needle = fold(query).trim();
  if (!needle) return options;
  return options.filter(
    (option) => fold(option.label).includes(needle) || fold(option.value).includes(needle),
  );
}

const PAGE = 10;

/** The open list's state and keys, shared by the inline field and the phone sheet. */
function useChoiceList(
  options: ReadonlyArray<ComboboxOption>,
  value: string,
  onChoose: (option: ComboboxOption) => void,
  onClose: () => void,
) {
  // null: the list shows every option (opened without typing).
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const matches = useMemo(() => filterComboboxOptions(options, query ?? ''), [options, query]);
  const reset = (nextQuery: string | null) => {
    const next = filterComboboxOptions(options, nextQuery ?? '');
    setQuery(nextQuery);
    setActive(
      nextQuery === null
        ? Math.max(
            next.findIndex((option) => option.value === value),
            0,
          )
        : next.length
          ? 0
          : -1,
    );
  };
  const move = (by: number) => {
    if (!matches.length) return;
    setActive((index) => Math.min(matches.length - 1, Math.max(0, (index < 0 ? -1 : index) + by)));
  };
  /** Returns true when the key was the list's. */
  const key = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return false;
    const moves: Record<string, number> = {
      ArrowDown: 1,
      ArrowUp: -1,
      PageDown: PAGE,
      PageUp: -PAGE,
    };
    if (event.key in moves && !(event.altKey && event.key.startsWith('Arrow'))) {
      event.preventDefault();
      move(moves[event.key]);
      return true;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const option = matches[active];
      if (option) onChoose(option);
      return true;
    }
    if (event.key === 'Escape' || (event.key === 'ArrowUp' && event.altKey)) {
      event.preventDefault();
      event.stopPropagation(); // closes the list, not a dialog around it
      onClose();
      return true;
    }
    return false;
  };
  return { query, active, setActive, matches, reset, key };
}

function ChoiceList({
  id,
  label,
  optionId,
  value,
  matches,
  active,
  query,
  hidden,
  className,
  onHover,
  onChoose,
}: {
  id: string;
  /** The field's label: the list's accessible name. */
  label: string;
  optionId: string;
  value: string;
  matches: ReadonlyArray<ComboboxOption>;
  active: number;
  query: string | null;
  hidden: boolean;
  className: string;
  onHover: (index: number) => void;
  onChoose: (option: ComboboxOption) => void;
}) {
  const copy = useNamespaceCopy(searchCopy);
  const appCopy = useChatCopy();
  const locale = useChatInterfaceLocale();
  const list = useRef<HTMLUListElement>(null);
  const noResults = formatCopy(copy.noResults, { query: query ?? '' });
  // Said politely once the person has typed: "3 results", or the no-results line.
  const status =
    hidden || query === null
      ? ''
      : matches.length
        ? formatCount(appCopy.activityResultsCount, matches.length, locale)
        : noResults;
  useEffect(() => {
    if (hidden || active < 0) return;
    list.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [hidden, active, matches]);
  return (
    <>
      <ul
        ref={list}
        id={id}
        role="listbox"
        aria-label={label}
        hidden={hidden}
        className={cn('m-0 list-none overflow-y-auto overscroll-contain p-1 text-sm', className)}
        // Keep focus in the search field: choosing, or dragging the list's
        // scrollbar, must not blur it and close the list first.
        onMouseDown={(event) => event.preventDefault()}
      >
        {matches.map((option, index) => (
          <li
            key={option.value}
            id={`${optionId}-${index}`}
            data-index={index}
            role="option"
            aria-selected={option.value === value}
            data-active={index === active || undefined}
            className="data-[active]:bg-accent data-[active]:text-accent-foreground flex min-h-9 cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 pointer-coarse:min-h-11"
            onMouseMove={() => onHover(index)}
            onClick={() => onChoose(option)}
          >
            <span className="min-w-0 flex-1 break-words">{option.label}</span>
            {option.value === value && <CheckIcon aria-hidden className="size-4 shrink-0" />}
          </li>
        ))}
        {!hidden && !matches.length && (
          <li role="presentation" aria-hidden className="text-muted-foreground p-2">
            {noResults}
          </li>
        )}
      </ul>
      <p role="status" className="sr-only">
        {status}
      </p>
    </>
  );
}

export interface ComboboxProps {
  /** The field's id, for the caller's <label htmlFor>. */
  id: string;
  /** The field's visible label: the phone sheet's title. */
  label: string;
  value: string;
  options: ReadonlyArray<ComboboxOption>;
  onChange: (value: string) => void;
  disabled?: boolean;
  describedBy?: string;
  testId?: string;
  /** Shown while the search is empty. */
  placeholder?: string;
  /** The field's classes, so it matches the form it sits in. */
  className?: string;
}

export function Combobox(props: ComboboxProps) {
  return useNarrowViewport() ? <SheetCombobox {...props} /> : <InlineCombobox {...props} />;
}

function InlineCombobox({
  id,
  label,
  value,
  options,
  onChange,
  disabled = false,
  describedBy,
  testId,
  placeholder,
  className,
}: ComboboxProps) {
  const listId = `${id}-list`;
  const optionId = `${id}-option`;
  const shown = options.find((option) => option.value === value)?.label ?? value;
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const choose = (option: ComboboxOption) => {
    close();
    if (option.value !== value) onChange(option.value);
  };
  const list = useChoiceList(options, value, choose, close);
  const show = (query: string | null) => {
    list.reset(query);
    setOpen(true);
  };
  const field = useRef<HTMLInputElement>(null);
  // An open list owns Escape before anything around it: a Radix dialog or sheet
  // listens on document in the capture phase, so the field's own handler would
  // be too late. Window capture runs first.
  useEffect(() => {
    if (!open) return;
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing || document.activeElement !== field.current)
        return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('keydown', onEscape, true);
    return () => window.removeEventListener('keydown', onEscape, true);
  }, [open]);
  return (
    <div
      className="relative min-w-0"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) close();
      }}
    >
      <input
        ref={field}
        id={id}
        data-testid={testId}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && list.active >= 0 ? `${optionId}-${list.active}` : undefined}
        aria-describedby={describedBy}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        disabled={disabled}
        placeholder={placeholder}
        value={open && list.query !== null ? list.query : shown}
        onChange={(event) => show((event.target as HTMLInputElement).value)}
        onClick={() => !open && show(null)}
        onFocus={(event) => (event.target as HTMLInputElement).select?.()}
        onKeyDown={(event) => {
          if (open) {
            if (event.key === 'Tab') close();
            else list.key(event);
          } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            show(null);
          } else if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
            // Like the select it replaces: Enter on the field never submits the form.
            event.preventDefault();
          }
        }}
        className={cn('w-full pr-9', className)}
      />
      <ChevronDownIcon
        aria-hidden
        className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2"
      />
      <ChoiceList
        id={listId}
        label={label}
        optionId={optionId}
        value={value}
        matches={list.matches}
        active={list.active}
        query={list.query}
        hidden={!open}
        className="border-border bg-popover text-popover-foreground absolute inset-x-0 top-[calc(100%+0.25rem)] z-20 max-h-72 rounded-md border shadow-(--shadow-lift)"
        onHover={list.setActive}
        onChoose={choose}
      />
    </div>
  );
}

function SheetCombobox({
  id,
  label,
  value,
  options,
  onChange,
  disabled = false,
  describedBy,
  testId,
  placeholder,
  className,
}: ComboboxProps) {
  const uid = useId().replace(/:/g, '');
  const searchId = `${uid}-search`;
  const listId = `${id}-list`;
  const optionId = `${id}-option`;
  const shown = options.find((option) => option.value === value)?.label ?? value;
  const [open, setOpen] = useState(false);
  const field = useRef<HTMLButtonElement>(null);
  const close = () => setOpen(false);
  const choose = (option: ComboboxOption) => {
    close();
    if (option.value !== value) onChange(option.value);
  };
  const list = useChoiceList(options, value, choose, close);
  return (
    <div className="relative min-w-0">
      <button
        ref={field}
        id={id}
        data-testid={testId}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        // Named "Time zone, Europe/Stockholm": the label alone would hide the value.
        aria-labelledby={`${uid}-name ${uid}-value`}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() => {
          list.reset(null);
          setOpen(true);
        }}
        // A touch target on phones even where the form's field is denser.
        className={cn('w-full pr-9 text-left', className, 'pointer-coarse:min-h-11')}
      >
        <span id={`${uid}-name`} className="sr-only">
          {label}
        </span>
        <span id={`${uid}-value`} className="block truncate">
          {shown}
        </span>
      </button>
      <ChevronDownIcon
        aria-hidden
        className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2"
      />
      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        returnFocus={field}
        title={<label htmlFor={searchId}>{label}</label>}
      >
        <input
          id={searchId}
          type="search"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={list.active >= 0 ? `${optionId}-${list.active}` : undefined}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="done"
          placeholder={placeholder}
          value={list.query ?? ''}
          onChange={(event) => list.reset((event.target as HTMLInputElement).value)}
          onKeyDown={(event) => {
            // Escape is the Dialog's: it closes and returns focus to the field.
            if (event.key !== 'Escape') list.key(event);
          }}
          className={cn('w-full', className)}
        />
        <ChoiceList
          id={listId}
          label={label}
          optionId={optionId}
          value={value}
          matches={list.matches}
          active={list.active}
          query={list.query}
          hidden={false}
          className="min-h-0 p-0"
          onHover={list.setActive}
          onChoose={choose}
        />
      </BottomSheet>
    </div>
  );
}
